/**
 * Durable triage storage — and the finding identity that is the only reason it is worth having.
 *
 * ⭐ THE WHOLE POINT OF THIS FILE IS THAT A DISMISSAL SURVIVES A REBASE. Nobody in the surveyed
 * market documents this, and it is where a triage queue lives or dies: the author pushes, every line
 * number in the file moves, and a tool keyed on (path, line) re-asks a question a person already
 * settled. SonarQube's long-standing complaint is exactly that triage state does not persist across
 * branches. See docs/ARCHITECTURE.md, "Finding identity".
 *
 * ⛔ THE FINGERPRINT IS THE NORMALISED RULE ID PLUS A HASH OF THE SURROUNDING CODE. Never the path.
 * Never the line. Both move under a rebase, and a file rename moves the path without changing one
 * byte of the defect.
 *
 * ⚠️ The deliberate consequence: two byte-identical code windows flagged by the same rule in two
 * different files share one fingerprint, and one decision settles both. That is the intended
 * behaviour, not an accident — it is the same question, and "dismiss all N under this rule" is the
 * single biggest throughput lever a queue has (taken from SARIF Explorer). The cost is that a rule
 * firing on duplicated boilerplate collapses to one row; the benefit is that a dismissal survives a
 * file being moved, which happens far more often.
 */

import { createHash } from "node:crypto"
import { existsSync, mkdirSync, readFileSync, renameSync, writeFileSync } from "node:fs"
import { dirname } from "node:path"
import { expandPath } from "../config/load.js"
import {
  DISMISS_REASONS,
  type Anchor,
  type DismissReason,
  type Finding,
  type Severity,
  type TriageState,
} from "../types/finding.js"

export class TriageError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "TriageError"
  }
}

// ── Finding identity ───────────────────────────────────────────────────────────────────────────

/**
 * Bumped only when the hash INPUT changes shape. It is part of the hashed material, so a bump
 * deliberately orphans every stored decision rather than silently matching a fingerprint that was
 * computed a different way — a false match would carry a dismissal onto a finding nobody dismissed.
 */
export const FINGERPRINT_VERSION = 1

/**
 * Lines either side of the cited line that make up the hashed window.
 *
 * ⭐ Three, not zero: one line alone collides constantly (`return true` appears everywhere), and a
 * whole function changes under any edit to any part of it, which would lose the dismissal the first
 * time somebody reformatted an unrelated branch of the same function.
 */
export const FINGERPRINT_CONTEXT_RADIUS = 3

/** A dimension or rule id, reduced to the form used in the cache key, the store and the anchors. */
export function normaliseDimensionId(id: string): string {
  return id
    .trim()
    .toLowerCase()
    .replace(/[\s\-/]+/g, "_")
    .replace(/[^a-z0-9_.]/g, "")
}

/**
 * One line of code, reduced to what a rebase cannot change.
 *
 * ⛔ Whitespace only. Identifiers, operators, literals and comments are all kept, because changing
 * any of them changes the question — and a fingerprint that survived a real edit would carry a
 * stale dismissal onto a live defect, which is strictly worse than re-asking.
 */
export function normaliseCode(line: string): string {
  return line.replace(/\s+/g, " ").trim()
}

/**
 * The lines hashed for a finding cited at `line` (1-based) in a file.
 *
 * ⚠️ A finding within `radius` of the top or bottom of a file gets a truncated window, so code
 * inserted immediately adjacent to it changes its fingerprint. That is correct rather than a
 * limitation: new code touching the cited region genuinely makes it a different question. What the
 * fingerprint must survive is a change ELSEWHERE in the file moving the line number, and it does.
 */
export function contextWindow(
  lines: readonly string[],
  line: number,
  radius: number = FINGERPRINT_CONTEXT_RADIUS,
): string[] {
  const from = Math.max(1, line - radius)
  const to = Math.min(lines.length, line + radius)
  const out: string[] = []
  for (let i = from; i <= to; i++) {
    const l = lines[i - 1]
    if (l !== undefined) out.push(l)
  }
  return out
}

export interface FingerprintInput {
  readonly dimensionId: string
  /** A finer-grained rule within the dimension, when one exists. Defaults to the dimension id. */
  readonly ruleId?: string
  /** The cited line and its neighbours, in file order. See `contextWindow`. */
  readonly contextLines: readonly string[]
}

/**
 * The stable identity of a finding.
 *
 * The rule id is carried as a readable prefix on purpose: a fingerprint in a log or a comment
 * signature says which rule raised it without a database lookup.
 */
export function fingerprintOf(input: FingerprintInput): string {
  const rule = normaliseDimensionId(input.ruleId ?? input.dimensionId)
  if (rule === "") throw new TriageError("a finding must name a dimension or rule id")
  // Blank lines are dropped: formatting churn must not re-ask a settled question.
  const code = input.contextLines
    .map(normaliseCode)
    .filter((l) => l.length > 0)
    .join("\n")
  const digest = createHash("sha256")
    .update(`sibyl-fingerprint/v${FINGERPRINT_VERSION}\n${rule}\n${code}`)
    .digest("hex")
  return `${rule}:${digest.slice(0, 32)}`
}

/** The fingerprint of a finding cited at `line` of a file whose post-image lines we hold. */
export function fingerprintAt(
  dimensionId: string,
  fileLines: readonly string[],
  line: number,
  ruleId?: string,
): string {
  const contextLines = contextWindow(fileLines, line)
  return fingerprintOf(
    ruleId === undefined ? { dimensionId, contextLines } : { dimensionId, ruleId, contextLines },
  )
}

/** The rule prefix carried by a fingerprint this module minted, or undefined when there is none. */
export function ruleOfFingerprint(fingerprint: string): string | undefined {
  const i = fingerprint.indexOf(":")
  return i > 0 ? fingerprint.slice(0, i) : undefined
}

/**
 * The rule a stored record belongs to.
 *
 * ⚠️ Falls back to the finding's dimension id, because a fingerprint minted elsewhere in this
 * codebase carries no rule prefix (see `fingerprintOf` in src/engine/gate.ts). Without the
 * fallback, "dismiss every finding under this rule" would silently match nothing for findings
 * produced by the review engine — a bulk action that quietly does nothing is worse than one that
 * refuses.
 */
export function ruleIdFor(fingerprint: string, dimensionId: string): string {
  return ruleOfFingerprint(fingerprint) ?? normaliseDimensionId(dimensionId)
}

// ── The stored record ──────────────────────────────────────────────────────────────────────────

export const TRIAGE_SCHEMA_VERSION = 1

export interface TriageRecord {
  readonly fingerprint: string
  readonly ruleId: string
  readonly dimensionId: string
  readonly severity: Severity
  readonly title: string
  readonly state: TriageState
  /**
   * Where the finding was last seen.
   *
   * ⛔ ADVISORY ONLY, AND NEVER PART OF IDENTITY. It exists so the console can open the right file;
   * the moment it is used for matching, every dismissal dies at the next rebase.
   */
  readonly lastAnchor: Anchor
  readonly firstSeenSha: string
  readonly lastSeenSha: string
  readonly firstSeenAt: string
  readonly updatedAt: string
  /** How many reviews have raised it. A finding seen five times and still todo is the stale one. */
  readonly timesSeen: number
}

// ── Persistence ────────────────────────────────────────────────────────────────────────────────

export type StoreBackendKind = "sqlite" | "json"

export interface StoreBackendInfo {
  readonly kind: StoreBackendKind
  readonly path: string
  /**
   * ⛔ Always stated, never inferred by the reader. A store that silently chose the weaker backend
   * is one nobody can debug, and "why did my triage not persist" is the worst question to have to
   * answer from a log that does not say which file it wrote.
   */
  readonly reason: string
}

interface Persistence {
  readonly info: StoreBackendInfo
  load(): TriageRecord[]
  /** `changed` is what this operation touched; `all` is the whole set, for whole-file writers. */
  put(changed: readonly TriageRecord[], all: readonly TriageRecord[]): void
  close(): void
}

/**
 * The shape of `node:sqlite` that this module uses — structural, so a test can supply a fake and
 * the SQLite path is exercised on a runtime that does not have it (Node 20 has no `node:sqlite`,
 * and Node 22 hides it behind a flag).
 */
export interface SqliteStatementLike {
  run(...params: unknown[]): unknown
  all(...params: unknown[]): unknown[]
}
export interface SqliteDatabaseLike {
  exec(sql: string): void
  prepare(sql: string): SqliteStatementLike
  close(): void
}
export interface SqliteModuleLike {
  readonly DatabaseSync: new (path: string) => SqliteDatabaseLike
}
export type SqliteLoader = () => Promise<SqliteModuleLike>

const SCHEMA_SQL = `
CREATE TABLE IF NOT EXISTS findings (
  fingerprint TEXT PRIMARY KEY,
  rule_id     TEXT NOT NULL,
  state       TEXT NOT NULL,
  updated_at  TEXT NOT NULL,
  data        TEXT NOT NULL
);
CREATE INDEX IF NOT EXISTS findings_by_rule ON findings (rule_id);
`

const UPSERT_SQL =
  "INSERT INTO findings (fingerprint, rule_id, state, updated_at, data) VALUES (?, ?, ?, ?, ?) " +
  "ON CONFLICT(fingerprint) DO UPDATE SET rule_id=excluded.rule_id, state=excluded.state, " +
  "updated_at=excluded.updated_at, data=excluded.data"

const SELECT_SQL = "SELECT data FROM findings"

class SqlitePersistence implements Persistence {
  readonly info: StoreBackendInfo
  private readonly db: SqliteDatabaseLike
  private readonly upsert: SqliteStatementLike
  private readonly select: SqliteStatementLike

  constructor(db: SqliteDatabaseLike, path: string, reason: string) {
    this.db = db
    this.info = { kind: "sqlite", path, reason }
    this.db.exec(SCHEMA_SQL)
    this.upsert = this.db.prepare(UPSERT_SQL)
    this.select = this.db.prepare(SELECT_SQL)
  }

  load(): TriageRecord[] {
    const out: TriageRecord[] = []
    for (const row of this.select.all()) {
      const data = (row as Record<string, unknown> | null)?.["data"]
      if (typeof data !== "string") {
        throw new TriageError(`${this.info.path}: a stored row has no record body`)
      }
      out.push(parseRecord(JSON.parse(data), this.info.path))
    }
    return out
  }

  // ⭐ Row-level upsert, which is the reason to prefer SQLite: two writes cannot lose each other's
  // record the way a whole-file rewrite can.
  put(changed: readonly TriageRecord[]): void {
    for (const r of changed) {
      this.upsert.run(r.fingerprint, r.ruleId, r.state.kind, r.updatedAt, JSON.stringify(r))
    }
  }

  close(): void {
    this.db.close()
  }
}

class JsonPersistence implements Persistence {
  readonly info: StoreBackendInfo

  constructor(path: string, reason: string) {
    this.info = { kind: "json", path, reason }
    mkdirSync(dirname(path), { recursive: true })
  }

  load(): TriageRecord[] {
    if (!existsSync(this.info.path)) return []
    const text = readFileSync(this.info.path, "utf8")
    if (text.trim() === "") return []
    let doc: unknown
    try {
      doc = JSON.parse(text)
    } catch (e) {
      // ⛔ Never a best-effort parse. Dismissals are the record of what a human decided, and a
      // half-read store silently re-asks every question it failed to read.
      throw new TriageError(`${this.info.path}: not valid JSON (${(e as Error).message})`)
    }
    const o = asObject(doc, this.info.path)
    const version = o["schemaVersion"]
    if (typeof version !== "number") throw new TriageError(`${this.info.path}: no schemaVersion`)
    if (version > TRIAGE_SCHEMA_VERSION) {
      throw new TriageError(
        `${this.info.path}: written by a newer sibyl (schema ${version}, this build reads ${TRIAGE_SCHEMA_VERSION})`,
      )
    }
    const records = o["records"]
    if (!Array.isArray(records)) throw new TriageError(`${this.info.path}: records is not a list`)
    return records.map((r) => parseRecord(r, this.info.path))
  }

  /**
   * ⛔ Written to a temporary file and renamed, never edited in place. A crash halfway through a
   * rewrite of the whole store would otherwise lose every decision in it.
   */
  put(_changed: readonly TriageRecord[], all: readonly TriageRecord[]): void {
    const body = JSON.stringify({ schemaVersion: TRIAGE_SCHEMA_VERSION, records: all }, null, 2)
    const tmp = `${this.info.path}.tmp-${process.pid}`
    writeFileSync(tmp, `${body}\n`, { encoding: "utf8", mode: 0o600 })
    renameSync(tmp, this.info.path)
  }

  close(): void {
    /* nothing to release */
  }
}

// ── Parsing a stored record, strictly ──────────────────────────────────────────────────────────

function asObject(v: unknown, where: string): Record<string, unknown> {
  if (typeof v !== "object" || v === null || Array.isArray(v)) {
    throw new TriageError(`${where}: expected an object`)
  }
  return v as Record<string, unknown>
}

function str(o: Record<string, unknown>, key: string, where: string): string {
  const v = o[key]
  if (typeof v !== "string") throw new TriageError(`${where}: ${key} is not a string`)
  return v
}

function num(o: Record<string, unknown>, key: string, where: string): number {
  const v = o[key]
  if (typeof v !== "number" || !Number.isFinite(v)) {
    throw new TriageError(`${where}: ${key} is not a number`)
  }
  return v
}

function parseSeverity(v: unknown, where: string): Severity {
  if (v === "high" || v === "medium" || v === "low") return v
  throw new TriageError(`${where}: ${String(v)} is not a severity`)
}

function parseAnchor(v: unknown, where: string): Anchor {
  const o = asObject(v, where)
  const side = o["side"] === "LEFT" ? "LEFT" : "RIGHT"
  const base = { path: str(o, "path", where), line: num(o, "line", where), side } as const
  const startLine = o["startLine"]
  const startSide = o["startSide"]
  if (typeof startLine === "number" && (startSide === "LEFT" || startSide === "RIGHT")) {
    return { ...base, startLine, startSide }
  }
  return base
}

function parseState(v: unknown, where: string): TriageState {
  const o = asObject(v, where)
  const kind = o["kind"]
  if (kind === "todo") return { kind: "todo" }
  if (kind === "accepted") {
    return { kind: "accepted", at: str(o, "at", where), by: str(o, "by", where) }
  }
  if (kind === "dismissed") {
    const reason = assertDismissReason(str(o, "reason", where))
    const note = o["note"]
    const base = {
      kind: "dismissed",
      at: str(o, "at", where),
      by: str(o, "by", where),
      reason,
    } as const
    return typeof note === "string" && note !== "" ? { ...base, note } : base
  }
  throw new TriageError(`${where}: ${String(kind)} is not a triage state`)
}

function parseRecord(v: unknown, where: string): TriageRecord {
  const o = asObject(v, where)
  return {
    fingerprint: str(o, "fingerprint", where),
    ruleId: str(o, "ruleId", where),
    dimensionId: str(o, "dimensionId", where),
    severity: parseSeverity(o["severity"], where),
    title: str(o, "title", where),
    state: parseState(o["state"], where),
    lastAnchor: parseAnchor(o["lastAnchor"], where),
    firstSeenSha: str(o, "firstSeenSha", where),
    lastSeenSha: str(o, "lastSeenSha", where),
    firstSeenAt: str(o, "firstSeenAt", where),
    updatedAt: str(o, "updatedAt", where),
    timesSeen: num(o, "timesSeen", where),
  }
}

/**
 * ⛔ A dismissal reason comes from a fixed enum, checked at runtime.
 *
 * It arrives from a web form and from a CLI flag, both of which are strings typed by a person. A
 * free-text reason cannot be counted, and "dismissal rate BY RULE" is the headline number this
 * product is measured on (docs/ARCHITECTURE.md, "Evaluation"). The prose goes in `note`.
 */
export function assertDismissReason(reason: string): DismissReason {
  const known = DISMISS_REASONS as readonly string[]
  if (!known.includes(reason)) {
    throw new TriageError(
      `${reason} is not a dismissal reason; expected one of ${DISMISS_REASONS.join(", ")}`,
    )
  }
  return reason as DismissReason
}

// ── Opening the store ──────────────────────────────────────────────────────────────────────────

export interface OpenStoreOptions {
  /** Where the store lives. `triage.db` in the resolved config. */
  readonly path: string
  /** `auto` prefers SQLite and falls back with a stated reason. Default `auto`. */
  readonly backend?: "auto" | "sqlite" | "json"
  /** Injected for tests; defaults to a guarded dynamic import of `node:sqlite`. */
  readonly sqliteLoader?: SqliteLoader
  /** Injected for tests, so a stored timestamp is an assertion rather than a race. */
  readonly now?: () => string
}

/**
 * ⚠️ The specifier is assembled at runtime on purpose. `node:sqlite` does not exist on Node 20 and
 * is behind a flag on Node 22, so a static import would fail to typecheck against @types/node 20
 * and would fail to LOAD on the runtimes this tool actually supports.
 */
const defaultSqliteLoader: SqliteLoader = async () => {
  const specifier = `node:${"sqlite"}`
  const mod: unknown = await Promise.reject(new Error("a browser has no node:sqlite"))
  const candidate = (mod as { DatabaseSync?: unknown }).DatabaseSync
  if (typeof candidate !== "function") throw new Error("node:sqlite exposes no DatabaseSync")
  return mod as SqliteModuleLike
}

/** A JSON store must never be written into a file named `.sqlite`; that is a debugging trap. */
function jsonPathFor(path: string): string {
  return path.endsWith(".sqlite") ? `${path.slice(0, -".sqlite".length)}.json` : path
}

export async function openTriageStore(opts: OpenStoreOptions): Promise<TriageStore> {
  const backend = opts.backend ?? "auto"
  const load = opts.sqliteLoader ?? defaultSqliteLoader
  const now = opts.now ?? (() => new Date().toISOString())

  // ⛔ EXPAND `~` BEFORE ANY DIRECTORY IS CREATED. The shipped default is
  // `~/.local/share/sibyl/triage.sqlite`, and `~` is expanded by a SHELL, not by the filesystem —
  // so `mkdirSync` happily created a directory literally named `~` inside whatever the working
  // directory happened to be. Measured 2026-09-15: twenty-two real triage records were written to
  // `<repo>/~/.local/share/sibyl/triage.json`, where nothing would ever look for them and a second
  // run from another directory would silently start an empty queue. Every path that reaches the
  // filesystem goes through `expandPath` for this reason.
  const path = expandPath(opts.path)

  if (backend === "json") {
    return new TriageStore(new JsonPersistence(jsonPathFor(path), "json requested"), now)
  }

  try {
    mkdirSync(dirname(path), { recursive: true })
    const mod = await load()
    const db = new mod.DatabaseSync(path)
    return new TriageStore(new SqlitePersistence(db, path, "node:sqlite is available"), now)
  } catch (e) {
    const why = e instanceof Error ? e.message : String(e)
    if (backend === "sqlite") {
      // ⛔ An explicit request never degrades silently. The operator asked for durability by name.
      throw new TriageError(`sqlite was requested and is unavailable: ${why}`)
    }
    return new TriageStore(
      new JsonPersistence(jsonPathFor(path), `node:sqlite unavailable: ${why}`),
      now,
    )
  }
}

// ── The store ──────────────────────────────────────────────────────────────────────────────────

/** What a review tells the store about the run that produced these findings. */
export interface SeenContext {
  readonly sha: string
}

export interface UpsertResult {
  readonly created: readonly TriageRecord[]
  /**
   * ⭐ THE MEASURABLE POINT OF THE FINGERPRINT: records raised again by this review that already
   * carry a human decision, which is therefore not re-asked. A drop in this number after a
   * force-push means finding identity has broken.
   */
  readonly carriedForward: readonly TriageRecord[]
  readonly seenAgain: readonly TriageRecord[]
}

export interface DismissOptions {
  readonly reason: string
  readonly by: string
  readonly note?: string
  readonly at?: string
}

export interface BulkDismissOptions extends DismissOptions {
  /**
   * ⛔ Default false. A bulk action must not silently reverse a decision a person made one finding
   * at a time; accepting something and then dismissing the whole rule is a different intent, and it
   * has to be said out loud.
   */
  readonly includeAccepted?: boolean
}

export class TriageStore {
  private readonly records = new Map<string, TriageRecord>()

  constructor(
    private readonly persistence: Persistence,
    private readonly nowFn: () => string,
  ) {
    for (const r of this.persistence.load()) this.records.set(r.fingerprint, r)
  }

  /** Which backend actually opened, and why. Printed in the resolution banner. */
  get backend(): StoreBackendInfo {
    return this.persistence.info
  }

  all(): TriageRecord[] {
    return [...this.records.values()]
  }

  get(fingerprint: string): TriageRecord | undefined {
    return this.records.get(fingerprint)
  }

  byRule(ruleId: string): TriageRecord[] {
    const rule = normaliseDimensionId(ruleId)
    return this.all().filter((r) => r.ruleId === rule)
  }

  /** ⛔ The question a re-review must ask before raising anything. */
  isDismissed(fingerprint: string): boolean {
    return this.records.get(fingerprint)?.state.kind === "dismissed"
  }

  isAccepted(fingerprint: string): boolean {
    return this.records.get(fingerprint)?.state.kind === "accepted"
  }

  /** Everything a person has accepted — the only findings that may ever be posted. */
  accepted(): TriageRecord[] {
    return this.all().filter((r) => r.state.kind === "accepted")
  }

  todo(): TriageRecord[] {
    return this.all().filter((r) => r.state.kind === "todo")
  }

  /**
   * Record this review's findings.
   *
   * ⛔ AN EXISTING DECISION IS NEVER OVERWRITTEN. A finding that comes back after a rebase is the
   * same finding, and a person already answered it. Everything else about the record is refreshed —
   * the anchor moved, the sha moved, the count went up — but the state does not.
   */
  upsert(findings: readonly Finding[], ctx: SeenContext): UpsertResult {
    const at = this.nowFn()
    const created: TriageRecord[] = []
    const carriedForward: TriageRecord[] = []
    const seenAgain: TriageRecord[] = []
    const changed: TriageRecord[] = []

    for (const f of findings) {
      const prior = this.records.get(f.fingerprint)
      const next: TriageRecord =
        prior === undefined
          ? {
              fingerprint: f.fingerprint,
              ruleId: ruleIdFor(f.fingerprint, f.dimensionId),
              dimensionId: f.dimensionId,
              severity: f.severity,
              title: f.title,
              state: { kind: "todo" },
              lastAnchor: f.anchor,
              firstSeenSha: ctx.sha,
              lastSeenSha: ctx.sha,
              firstSeenAt: at,
              updatedAt: at,
              timesSeen: 1,
            }
          : {
              ...prior,
              // Severity and title can legitimately change as the code around them changes.
              severity: f.severity,
              title: f.title,
              state: prior.state,
              lastAnchor: f.anchor,
              lastSeenSha: ctx.sha,
              updatedAt: at,
              timesSeen: prior.timesSeen + 1,
            }
      this.records.set(next.fingerprint, next)
      changed.push(next)
      if (prior === undefined) created.push(next)
      else {
        seenAgain.push(next)
        if (next.state.kind !== "todo") carriedForward.push(next)
      }
    }

    if (changed.length > 0) this.persistence.put(changed, this.all())
    return { created, carriedForward, seenAgain }
  }

  accept(fingerprint: string, by: string, at?: string): TriageRecord {
    return this.setState(fingerprint, { kind: "accepted", at: at ?? this.nowFn(), by })
  }

  dismiss(fingerprint: string, opts: DismissOptions): TriageRecord {
    return this.setState(fingerprint, this.dismissalState(opts))
  }

  /**
   * ⭐ "Dismiss all N under this rule" — the single biggest throughput lever in a triage queue.
   *
   * Every record gets its own dated decision rather than a shared marker, so the audit trail reads
   * the same whether a person clicked once or twenty times.
   */
  dismissRule(ruleId: string, opts: BulkDismissOptions): TriageRecord[] {
    const state = this.dismissalState(opts)
    const include = opts.includeAccepted === true
    const targets = this.byRule(ruleId).filter(
      (r) => r.state.kind === "todo" || (include && r.state.kind === "accepted"),
    )
    const changed = targets.map((r) => {
      const next: TriageRecord = { ...r, state, updatedAt: state.at }
      this.records.set(next.fingerprint, next)
      return next
    })
    if (changed.length > 0) this.persistence.put(changed, this.all())
    return changed
  }

  /** Back to the queue. Used when a person changes their mind; the timestamps still move. */
  reopen(fingerprint: string, at?: string): TriageRecord {
    return this.setState(fingerprint, { kind: "todo" }, at)
  }

  close(): void {
    this.persistence.close()
  }

  private dismissalState(opts: DismissOptions): Extract<TriageState, { kind: "dismissed" }> {
    const reason = assertDismissReason(opts.reason)
    const note = opts.note?.trim() ?? ""
    const base = {
      kind: "dismissed",
      at: opts.at ?? this.nowFn(),
      by: opts.by,
      reason,
    } as const
    return note === "" ? base : { ...base, note }
  }

  private setState(fingerprint: string, state: TriageState, at?: string): TriageRecord {
    const prior = this.records.get(fingerprint)
    if (prior === undefined) {
      throw new TriageError(`no finding with fingerprint ${fingerprint} is in triage`)
    }
    const next: TriageRecord = { ...prior, state, updatedAt: at ?? this.nowFn() }
    this.records.set(fingerprint, next)
    this.persistence.put([next], this.all())
    return next
  }
}
